import { For } from "solid-js";
import {
  abilityDef,
  otherPlayer,
  pieceDef,
  type Card,
  type GameState,
  type PlayerId,
} from "../../core";
import { PLAYER_ACCENT } from "../../app/config";
import type { GameController } from "../../app/controller";
import { PieceIcon } from "./PieceIcon";

/**
 * The deck tracker. Each player's full roster is public from the start (only the
 * live hand/deck split is hidden), so both sides can see what the other drafted
 * and follow it down: how many of each card are still to come (in hand or deck),
 * how many are deployed/played, and how many have been lost.
 */

interface Row {
  key: string;
  kind: "piece" | "ability";
  type: string;
  name: string;
  total: number;
}

function rosterRows(roster: readonly Card[]): Row[] {
  const map = new Map<string, Row>();
  for (const c of roster) {
    const key = c.kind === "piece" ? `p:${c.piece}` : `a:${c.ability}`;
    const found = map.get(key);
    if (found) found.total++;
    else
      map.set(key, {
        key,
        kind: c.kind,
        type: c.kind === "piece" ? c.piece : c.ability,
        name: c.kind === "piece" ? pieceDef(c.piece).name : abilityDef(c.ability).name,
        total: 1,
      });
  }
  return [...map.values()].sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === "piece" ? -1 : 1;
    if (a.kind === "piece" && b.kind === "piece") {
      return pieceDef(b.type as never).value - pieceDef(a.type as never).value;
    }
    return a.name.localeCompare(b.name);
  });
}

/** How many of this card the player has committed (deployed pieces / played abilities). */
function usedCount(state: GameState, pid: PlayerId, row: Row): number {
  let n = 0;
  for (const e of state.log) {
    if (!("player" in e) || e.player !== pid) continue;
    if (row.kind === "piece" && e.kind === "deploy" && e.piece === row.type) n++;
    else if (row.kind === "ability" && e.kind === "card" && e.ability === row.type) n++;
  }
  return n;
}

/** How many of this piece type the player has lost (recorded in the foe's captures). */
function lostCount(state: GameState, pid: PlayerId, type: string): number {
  return state.players[otherPlayer(pid)].captured.filter((t) => t === type).length;
}

function TrackerSide(props: { controller: GameController; owner: PlayerId }) {
  const state = () => props.controller.store.state;
  const rows = () => rosterRows(state().players[props.owner].roster);
  const name = () => state().players[props.owner].name;
  const left = (r: Row) => r.total - usedCount(state(), props.owner, r);
  // This side's fallen pieces (recorded in the foe's capture pile), heaviest first.
  const fallen = () =>
    [...state().players[otherPlayer(props.owner)].captured].sort(
      (a, b) => pieceDef(b).value - pieceDef(a).value,
    );

  return (
    <div class="dt-side">
      <div class="dt-head">
        <span class="swatch" style={{ background: PLAYER_ACCENT[props.owner] }} />
        <span class="pname">{name()}</span>
        <span class="dt-legend">left · out · lost</span>
      </div>
      <ul class="dt-rows">
        <For each={rows()}>
          {(r) => {
            const used = () => usedCount(state(), props.owner, r);
            const lost = () => (r.kind === "piece" ? lostCount(state(), props.owner, r.type) : 0);
            const out = () => Math.max(0, used() - lost());
            return (
              <li class="dt-row" classList={{ spent: left(r) === 0 && used() > 0 }}>
                <span class="dt-icon">
                  {r.kind === "piece" ? (
                    <PieceIcon type={r.type as never} owner={props.owner} />
                  ) : (
                    <span class="glyph-ability">{abilityDef(r.type as never).glyph}</span>
                  )}
                </span>
                <span class="dt-name">{r.name}</span>
                <span class="dt-counts">
                  <span class="dt-n left" title="in hand or deck">{left(r)}</span>
                  <span class="dt-sep">·</span>
                  <span class="dt-n out" title="deployed / in play">{out()}</span>
                  <span class="dt-sep">·</span>
                  <span class="dt-n lost" classList={{ zero: lost() === 0 }} title="lost">
                    {lost()}
                  </span>
                </span>
              </li>
            );
          }}
        </For>
      </ul>
      <div class="dt-grave">
        <span class="dt-grave-label">Lost</span>
        <span class="dt-grave-pieces">
          <For each={fallen()} fallback={<span class="dt-grave-none">none yet</span>}>
            {(type) => <PieceIcon type={type} owner={props.owner} />}
          </For>
        </span>
      </div>
    </div>
  );
}

export function DeckTracker(props: { controller: GameController }) {
  const c = props.controller;
  // Opponent on top, you on the bottom — mirrors the board.
  const top = () => otherPlayer(c.viewer());
  return (
    <div class="decktracker">
      <TrackerSide controller={c} owner={top()} />
      <TrackerSide controller={c} owner={c.viewer()} />
    </div>
  );
}
